import { GridSortItem } from "@mui/x-data-grid";
import { useState } from "react";
import { useParams } from "react-router-dom";
import CustomDataGridV2 from "../../../../../../../../components/CustomDataGridV2";
import RenderWithTooltip, {
  CustomTrimmedTooltip,
} from "../../../../../../../../components/RenderWithTooltip";
import ActionMenu from "../../../../../../../../components/app/ActionMenu";
import DeleteModal, {
  useDeleteDisClosure,
} from "../../../../../../../../components/app/DeleteModal";
import { useRoleController } from "../../../../../../../../components/useRoleController";
import useDisclosure from "../../../../../../../../hooks/use-disclosure";
import { useQueryParams } from "../../../../../../../../hooks/use-query-param";
import { useArchiveMedication } from "../../../../../../../../queries/medication/use-patient-medication-archive";
import { usePatientMedicationList } from "../../../../../../../../queries/medication/use-patient-medication-list";
import { formatDate } from "../../../../../../../../utils/date-time-utility";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  applyDefaultColumnSettings,
  filterActionCol,
  getIdFromObj,
  getValueFromObj,
  getXtenantId,
  replaceUnderScoreAddSpace,
  roleFilter,
} from "../../../../../../../../utils/utils";
import UpsertMedicationsDrawer from "../../medication/UpsertMedicationsDrawer";
import { Medication } from "../../medication/medication-constant";
import {
  MEDICATION_DOSAGE_LIST_MAPPER,
  MEDICATION_ROUTE_LIST_MAPPER,
} from "./ClinicalLayout-constant";

interface MedicationsListProps {
  isInsideVisitNote?: boolean;
}

const MedicationsList = ({
  isInsideVisitNote = false,
}: MedicationsListProps) => {
  const [pagination, setPagination] = useState<DGT_Pagination>(DEF_PAGINATION);
  const [gridSorting, setGridSorting] = useState<GridSortItem[]>([]);
  const { patientId } = useParams() as { patientId: any };
  const { mutateAsync: archiveMedication } = useArchiveMedication();
  const editMedicationDisclosure = useDisclosure(false);
  const { hasReadOnly, hasUpdate, hasDelete } = useRoleController("Clinical");
  const showPastMedications = useQueryParams("showPastMedications") === "true";

  const { data: medicationList, refetch } = usePatientMedicationList({
    patientUuid: patientId,
    pagination,
    gridSorting,
    archive: false,
    active: showPastMedications ? false : true,
    xTENANTID: getXtenantId() ?? undefined,
  });

  const deleteMedicationDisclosure = useDeleteDisClosure();

  const COLUMNS = applyDefaultColumnSettings<Medication>(
    [
      {
        field: "medicalCodes",
        headerName: "Medicine",
        flex: 0.35,
        minWidth: 150,
        renderCell: ({ value }) =>
          value ? <CustomTrimmedTooltip value={getValueFromObj(value)} /> : "-",
      },
      {
        field: "directions",
        headerName: "Directions",
        flex: 0.65,
        minWidth: 260,
        renderCell: MedicationsDirection,
      },
      {
        field: "prescribedBy",
        headerName: "Prescribed By",
        flex: 0.5,
        minWidth: 140,
        renderCell: ({ value }) =>
          Object?.keys(value ?? {})?.length > 0
            ? (Object.values(value)?.[0] as any)
            : "-",
      },
      {
        field: "medication_start_date",
        headerName: "Start Date",
        flex: 0.4,
        minWidth: 100,
        renderCell: ({ row }) =>
          row?.medicationStartDate ? formatDate(row?.medicationStartDate) : "-",
      },
      {
        field: "medication_last_date",
        headerName: "End Date",
        flex: 0.4,
        minWidth: 100,
        renderCell: ({ row }) =>
          row?.lifeTime
            ? "Lifetime"
            : row?.medicationLastDate
              ? formatDate(row?.medicationLastDate)
              : "-",
      },
      {
        field: "note",
        headerName: "Note",
        flex: 0.5,
        minWidth: 120,
        renderCell: ({ value }) => <RenderWithTooltip value={value} />,
      },
      {
        field: "Action",
        headerName: "Actions",
        width: 70,
        hideSortIcons: true,
        renderCell: ({ row }) => {
          if ((row as any)?.electronicallyPrescribed) return;

          return (
            <ActionMenu
              options={roleFilter({
                list: ACTION_MENU_OPTIONS,
                compWith: {
                  edit: hasUpdate,
                  delete: hasDelete,
                },
              })}
              onActionClick={(v) => {
                if (v === "edit") {
                  let prescribedBy = undefined;

                  Object.entries(row?.prescribedBy ?? {})?.forEach(
                    ([value, label]) => {
                      prescribedBy = { value: value, label: label };
                    }
                  );

                  const transformedMedicationData: any = {
                    ...row,
                    prescribedBy,
                    medicalCodes: getIdFromObj(row?.medicalCodes),
                  };

                  editMedicationDisclosure.open(transformedMedicationData);
                } else deleteMedicationDisclosure.open(row?.uuid);
              }}
            />
          );
        },
      },
    ],
    ["medication_start_date", "medication_last_date"]
  );

  function archiveHandler<T>(value: boolean, id: T) {
    return archiveMedication({
      patientMedicationId: id?.toString() ?? "",
      status: value,
      xTENANTID: getXtenantId() ?? undefined,
    }).then((res) => {
      refetch();
      return res;
    });
  }

  return (
    <>
      <UpsertMedicationsDrawer disclosureHost={editMedicationDisclosure} />

      <DeleteModal
        w="380px"
        open={deleteMedicationDisclosure.opened}
        onClose={deleteMedicationDisclosure.close}
        id={deleteMedicationDisclosure.deleteId}
        onConfirmation={(id) => archiveHandler(true, id)}
        content={"You want to delete this Medication"}
      />

      {/* <ChartTableWrapper> */}
      <CustomDataGridV2
        getRowId={(row) => row?.uuid}
        rows={medicationList?.data?.content ?? []}
        rowCount={medicationList?.data?.page?.totalElements ?? 10}
        columns={filterActionCol(COLUMNS, hasReadOnly || isInsideVisitNote)}
        // sx={{ marginTop: "40px" }}
        disableRowSelectionOnClick
        paginationModel={pagination}
        onPaginationModelChange={setPagination}
        sortModel={gridSorting}
        onSortModelChange={setGridSorting}
      />
      {/* </ChartTableWrapper> */}
    </>
  );
};

export default MedicationsList;

type MedicationsDirectionProps = {
  row: Medication;
};

const MedicationsDirection = ({ row }: MedicationsDirectionProps) => {
  const calcDays = () => {
    if (!row?.medicationStartDate || !row?.medicationLastDate) return 0;
    const startDate = new Date(row?.medicationStartDate);
    const endDate = new Date(row?.medicationLastDate);
    const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const medicationData = `${row?.quantity ? "Take " + row?.quantity : ""} ${
    MEDICATION_DOSAGE_LIST_MAPPER[row?.dosage ?? ""] ?? ""
  } ${
    row?.dosageWhen
      ? replaceUnderScoreAddSpace(row?.dosageWhen?.toLocaleLowerCase())
      : ""
  } ${
    row?.direction
      ? "by " + MEDICATION_ROUTE_LIST_MAPPER[row?.direction] + " Route"
      : ""
  } ${
    row?.frequency?.toLocaleLowerCase()
      ? replaceUnderScoreAddSpace(row?.frequency?.toLocaleLowerCase())
      : ""
  } For ${
    row?.lifeTime ? "lifetime" : calcDays() + " days"
  }, starting from ${formatDate(row?.medicationStartDate)}`;

  return (
    <RenderWithTooltip value={medicationData?.trim() ? medicationData : "-"} />
  );
};

const ACTION_MENU_OPTIONS = [
  { value: "edit", label: "Edit" },
  { value: "delete", label: "Delete" },
];
